// src/pages/ChatBot/query-logs/inspect-trace-helpers.ts
//
// Pure helpers for the Inspect modal's retrieval-trace explorer: stage
// metadata, per-stage ordering, score normalization, and rank-trajectory
// computation. No React and no side effects — everything is data in/data out.

import type { ChunkTrace, QueryLogDoc } from './types';
import { MAX_FULL_TEXT_TERMS, getMatchedTerms, tokenize } from './lexical';

export type TraceStage = 'vector' | 'text' | 'fused' | 'rerank' | 'final';
export type TraceSortMode = 'rank' | 'score';

// Sentinel the detail endpoint substitutes for a chunk whose text no longer resolves
// (superseded / role-blocked) — such chunks get no keyword-match entry.
export const CHUNK_UNAVAILABLE_TEXT = '[chunk no longer available]';

export interface StageMeta {
  stage: TraceStage;
  label: string;
  shortLabel: string;
  hasScore: boolean;
}

const STAGE_META: Record<TraceStage, StageMeta> = {
  vector: { stage: 'vector', label: 'Vector', shortLabel: 'V', hasScore: true },
  text: { stage: 'text', label: 'Text', shortLabel: 'T', hasScore: false },
  fused: { stage: 'fused', label: 'Fused', shortLabel: 'F', hasScore: true },
  rerank: { stage: 'rerank', label: 'Rerank', shortLabel: 'R', hasScore: true },
  final: { stage: 'final', label: 'Final', shortLabel: '#', hasScore: false },
};

// Pipeline order — also the reading order of the rank-trajectory strip.
export const TRACE_STAGES: StageMeta[] = [
  STAGE_META.vector,
  STAGE_META.text,
  STAGE_META.fused,
  STAGE_META.rerank,
  STAGE_META.final,
];

// Rerank is an LLM judge on a fixed 0-100 scale (vector/fused normalize
// against the observed stage max instead).
export const RERANK_SCALE_MAX = 100;

export function getStageMeta(stage: TraceStage): StageMeta {
  return STAGE_META[stage];
}

export function getStageRank(chunk: ChunkTrace, stage: TraceStage): number | undefined {
  return chunk.rank[stage];
}

// Which retrieval axis (or both) originally surfaced this chunk, derived from vector/text rank presence.
// Every fused/reranked/final chunk descends from at least one axis, so `undefined` is effectively unreachable.
export type FoundBy = 'vector' | 'keyword' | 'both';

export function getFoundBy(chunk: ChunkTrace): FoundBy | undefined {
  const inVector = chunk.rank.vector !== undefined;
  const inText = chunk.rank.text !== undefined;
  if (inVector && inText) return 'both';
  if (inVector) return 'vector';
  if (inText) return 'keyword';
  return undefined;
}

export function getStageScore(chunk: ChunkTrace, stage: TraceStage): number | undefined {
  if (stage === 'text' || stage === 'final') return undefined;
  return chunk.score[stage];
}

// A chunk missing both rank and score on an axis simply wasn't present at
// that stage of the pipeline.
export function isPresentAtStage(chunk: ChunkTrace, stage: TraceStage): boolean {
  return getStageRank(chunk, stage) !== undefined || getStageScore(chunk, stage) !== undefined;
}

export function countChunksAtStage(chunks: ChunkTrace[], stage: TraceStage): number {
  return chunks.filter((chunk) => isPresentAtStage(chunk, stage)).length;
}

// Default lens: the served order when it exists, else the deepest stage the
// pipeline reached.
export function getDefaultStage(chunks: ChunkTrace[]): TraceStage {
  if (chunks.some((chunk) => chunk.rank.final !== undefined)) return 'final';
  if (countChunksAtStage(chunks, 'rerank') > 0) return 'rerank';
  return 'fused';
}

// The term list the trace matches against chunk text. Persisted searchTerms are the
// ground truth for new docs; older docs fall back to tokenizing standaloneQuery
// (capped, mirroring maxFullTextTerms) with derived: true — but only when the doc
// demonstrably ran the text axis (some chunk has rank.text), so vector-only logs
// never show phantom keywords.
export interface ResolvedSearchTerms {
  terms: string[];
  derived: boolean;
}

export function resolveSearchTerms(
  log: Pick<QueryLogDoc, 'searchTerms' | 'standaloneQuery' | 'chunks'>,
): ResolvedSearchTerms {
  if (log.searchTerms !== undefined && log.searchTerms.length > 0) {
    return { terms: log.searchTerms, derived: false };
  }
  const textAxisRan = (log.chunks ?? []).some((chunk) => chunk.rank.text !== undefined);
  if (textAxisRan && log.standaloneQuery) {
    return { terms: tokenize(log.standaloneQuery).slice(0, MAX_FULL_TEXT_TERMS), derived: true };
  }
  return { terms: [], derived: false };
}

// Matched terms per chunkId. Chunks whose text is missing or the unavailable
// sentinel get NO entry — the card renders a "matches unknown" state, distinct
// from an empty match list (a real "no keyword overlap").
export function buildMatchedTermsByChunk(
  chunks: ChunkTrace[],
  chunkTexts: Record<string, string>,
  terms: string[],
): ReadonlyMap<string, string[]> {
  const matches = new Map<string, string[]>();
  if (terms.length === 0) return matches;
  for (const chunk of chunks) {
    const text = chunkTexts[chunk.chunkId];
    if (text === undefined || text === CHUNK_UNAVAILABLE_TEXT) continue;
    matches.set(chunk.chunkId, getMatchedTerms(text, terms));
  }
  return matches;
}

export interface StagePartition {
  present: ChunkTrace[];
  absent: ChunkTrace[];
}

// Splits chunks into those present at the stage (ordered through the lens)
// and those absent (kept in original trace order for the dimmed bucket).
// coverageByChunk (matched-term counts) enables 'score' sort on the rank-only
// text axis: matches desc, ties broken by text rank asc, unknown-coverage last.
export function partitionChunksForStage(
  chunks: ChunkTrace[],
  stage: TraceStage,
  sortMode: TraceSortMode,
  coverageByChunk?: ReadonlyMap<string, number>,
): StagePartition {
  const present = chunks.filter((chunk) => isPresentAtStage(chunk, stage));
  const absent = chunks.filter((chunk) => !isPresentAtStage(chunk, stage));
  if (sortMode === 'score' && stage === 'text' && coverageByChunk !== undefined) {
    present.sort(
      (a, b) =>
        compareWithUndefinedLast(coverageByChunk.get(a.chunkId), coverageByChunk.get(b.chunkId), 'desc') ||
        compareByRankAsc(a, b, stage),
    );
    return { present, absent };
  }
  const useScoreSort = sortMode === 'score' && getStageMeta(stage).hasScore;
  present.sort((a, b) =>
    useScoreSort ? compareByScoreDesc(a, b, stage) : compareByRankAsc(a, b, stage),
  );
  return { present, absent };
}

function compareByRankAsc(a: ChunkTrace, b: ChunkTrace, stage: TraceStage): number {
  return compareWithUndefinedLast(getStageRank(a, stage), getStageRank(b, stage), 'asc');
}

function compareByScoreDesc(a: ChunkTrace, b: ChunkTrace, stage: TraceStage): number {
  return compareWithUndefinedLast(getStageScore(a, stage), getStageScore(b, stage), 'desc');
}

function compareWithUndefinedLast(
  a: number | undefined,
  b: number | undefined,
  direction: 'asc' | 'desc',
): number {
  if (a === undefined && b === undefined) return 0;
  if (a === undefined) return 1;
  if (b === undefined) return -1;
  return direction === 'asc' ? a - b : b - a;
}

export function getStageMaxScore(chunks: ChunkTrace[], stage: TraceStage): number | undefined {
  const scores = chunks
    .map((chunk) => getStageScore(chunk, stage))
    .filter((score): score is number => score !== undefined);
  return scores.length > 0 ? Math.max(...scores) : undefined;
}

// Bar-fill fraction in [0, 1]. Vector and fused normalize against the stage
// max across chunks; rerank against its fixed 0-100 scale.
export function normalizeScoreFraction(
  score: number,
  stage: TraceStage,
  stageMaxScore: number | undefined,
): number {
  const scaleMax = stage === 'rerank' ? RERANK_SCALE_MAX : stageMaxScore;
  if (scaleMax === undefined || scaleMax <= 0) return 0;
  return Math.min(1, Math.max(0, score / scaleMax));
}

// Raw-value display precision per axis: vector = cosine (3dp), fused = RRF
// small decimals (4dp), rerank = integer 0-100.
export function formatStageScore(score: number, stage: TraceStage): string {
  if (stage === 'vector') return score.toFixed(3);
  if (stage === 'fused') return score.toFixed(4);
  return String(Math.round(score));
}

export type RankDirection = 'improved' | 'worsened' | 'neutral';

export interface TrajectoryCell {
  stage: TraceStage;
  shortLabel: string;
  rank?: number;
  direction: RankDirection;
  title: string;
}

// The chunk's journey through the pipeline, read left to right (V T F R #).
// Direction compares each rank against the previous stage the chunk actually
// appeared in — lower rank = improved.
export function buildRankTrajectory(chunk: ChunkTrace): TrajectoryCell[] {
  let previousRank: number | undefined;
  return TRACE_STAGES.map((meta) => {
    const rank = getStageRank(chunk, meta.stage);
    let direction: RankDirection = 'neutral';
    if (rank !== undefined && previousRank !== undefined) {
      if (rank < previousRank) direction = 'improved';
      else if (rank > previousRank) direction = 'worsened';
    }
    if (rank !== undefined) previousRank = rank;
    const title =
      rank !== undefined
        ? `${meta.label} rank ${rank}`
        : `Not present at the ${meta.label.toLowerCase()} stage`;
    return { stage: meta.stage, shortLabel: meta.shortLabel, rank, direction, title };
  });
}
